import React from 'react';
import { PayoffMonthlyData } from '../types';

interface PayoffDataTableProps {
  data: PayoffMonthlyData[];
}

const formatCurrency = (value: number) => {
    if (Math.abs(value) < 0.01) value = 0;
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        notation: 'compact',
        maximumSignificantDigits: 3,
    }).format(value);
};

export const PayoffDataTable: React.FC<PayoffDataTableProps> = ({ data }) => {
  if (!data || data.length === 0) {
    return null;
  }
  
  const yearlyDataMap = new Map<number, PayoffMonthlyData>();
  data.forEach(row => {
    if (row.month % 12 === 0 || row.month === data.length) {
      yearlyDataMap.set(row.year, row);
    }
  });

  if (!yearlyDataMap.has(data[0].year) && data.length < 12) {
      yearlyDataMap.set(data[0].year, data[data.length-1]);
  }
  
  const yearlyData = Array.from(yearlyDataMap.values()).sort((a, b) => a.year - b.year);

  return (
    <div className="mt-8 bg-white dark:bg-slate-800 p-6 rounded-xl shadow-md border border-gray-200 dark:border-slate-700 transition-colors">
      <div className="flex justify-between items-center mb-4 border-b dark:border-slate-600 pb-2">
        <h3 className="text-xl font-bold text-bp-slate dark:text-white">Year-by-Year Wealth Projection</h3>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm text-left text-gray-500 dark:text-gray-400">
          <thead className="text-xs text-gray-700 dark:text-gray-200 uppercase bg-gray-100 dark:bg-slate-700 sticky top-0">
            <tr>
              <th scope="col" rowSpan={2} className="px-2 py-3 align-middle text-center bg-gray-100 dark:bg-slate-700 border-b border-gray-200 dark:border-slate-600 sticky left-0 z-10">Year</th>
              <th scope="col" colSpan={5} className="px-6 py-3 text-center border-l border-r border-gray-200 dark:border-slate-600 bg-blue-50 dark:bg-blue-900/40">Pay Off Early Scenario</th>
              <th scope="col" colSpan={5} className="px-6 py-3 text-center border-l border-r border-gray-200 dark:border-slate-600 bg-green-50 dark:bg-green-900/40">Invest Scenario</th>
            </tr>
            <tr>
              <th scope="col" className="px-2 py-3 text-right bg-blue-50 dark:bg-blue-900/30 border-l border-gray-200 dark:border-slate-600">Balance</th>
              <th scope="col" className="px-2 py-3 text-right bg-blue-50 dark:bg-blue-900/30">Home Equity</th>
              <th scope="col" className="px-2 py-3 text-right bg-blue-50 dark:bg-blue-900/30">Investments</th>
              <th scope="col" className="px-2 py-3 text-right bg-blue-50 dark:bg-blue-900/30">Tax Savings</th>
              <th scope="col" className="px-2 py-3 font-semibold text-right bg-blue-50 dark:bg-blue-900/30 border-r border-gray-200 dark:border-slate-600">Net Worth</th>
              
              <th scope="col" className="px-2 py-3 text-right bg-green-50 dark:bg-green-900/30 border-l border-gray-200 dark:border-slate-600">Balance</th>
              <th scope="col" className="px-2 py-3 text-right bg-green-50 dark:bg-green-900/30">Home Equity</th>
              <th scope="col" className="px-2 py-3 text-right bg-green-50 dark:bg-green-900/30">Investments</th>
               <th scope="col" className="px-2 py-3 text-right bg-green-50 dark:bg-green-900/30">Tax Savings</th>
              <th scope="col" className="px-2 py-3 font-semibold text-right bg-green-50 dark:bg-green-900/30 border-r border-gray-200 dark:border-slate-600">Net Worth</th>
            </tr>
          </thead>
          <tbody>
            {yearlyData.map((row) => (
              <tr key={row.year} className="bg-white dark:bg-slate-800 border-b dark:border-slate-700 hover:bg-gray-50 dark:hover:bg-slate-700">
                <td className="px-2 py-4 font-medium text-gray-900 dark:text-white text-center sticky left-0 bg-white dark:bg-slate-800 border-r border-gray-200 dark:border-slate-700">{row.year}</td>
                {/* Payoff Scenario - Order: Balance, Equity, Investments, Tax, Net Worth */}
                <td className="px-2 py-4 text-right bg-blue-50/50 dark:bg-blue-900/10 border-l border-gray-200 dark:border-slate-600">{formatCurrency(row.payoffBalance)}</td>
                <td className="px-2 py-4 text-right bg-blue-50/50 dark:bg-blue-900/10">{formatCurrency(row.payoffHomeEquity)}</td>
                <td className="px-2 py-4 text-right bg-blue-50/50 dark:bg-blue-900/10">{formatCurrency(row.payoffInvestBalance)}</td>
                <td className="px-2 py-4 text-right bg-blue-50/50 dark:bg-blue-900/10">{formatCurrency(row.payoffTaxSavingsThisYear)}</td>
                <td className="px-2 py-4 font-semibold text-right bg-blue-50/50 dark:bg-blue-900/10 border-r border-gray-200 dark:border-slate-600">{formatCurrency(row.payoffNetWorth)}</td>
                
                {/* Invest Scenario - Order: Balance, Equity, Investments, Tax, Net Worth */}
                <td className="px-2 py-4 text-right bg-green-50/50 dark:bg-green-900/10 border-l border-gray-200 dark:border-slate-600">{formatCurrency(row.originalBalance)}</td>
                <td className="px-2 py-4 text-right bg-green-50/50 dark:bg-green-900/10">{formatCurrency(row.investHomeEquity)}</td>
                <td className="px-2 py-4 text-right bg-green-50/50 dark:bg-green-900/10">{formatCurrency(row.investBalance)}</td>
                <td className="px-2 py-4 text-right bg-green-50/50 dark:bg-green-900/10">{formatCurrency(row.taxSavingsThisYear)}</td>
                <td className="px-2 py-4 font-semibold text-right text-green-700 dark:text-green-400 bg-green-50/50 dark:bg-green-900/10 border-r border-gray-200 dark:border-slate-600">{formatCurrency(row.investNetWorth)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};